import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { UserService } from 'src/app/shared/services/user.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { jsPDF } from 'jspdf';
import { TableColumn } from 'src/app/shared/components/data-table/data-table.component';
import {
  EvaluationDetails,
  BidInfo,
  AssignmentInfo,
  TimelineEvent,
  Document,
  MeetingAttachment
} from 'src/app/shared/models/app.models';

@Component({
  selector: 'app-evaluation-details',
  templateUrl: './evaluation-details.component.html',
  styleUrls: ['./evaluation-details.component.scss']
})
export class EvaluationDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  evaluationId: string = '';
  evaluationDetails: EvaluationDetails | null = null;
  tableColumns: TableColumn[] = [];
  totalBids: number = 0;
  showDocumentsDialog: boolean = false;
  documents: Document[] = [];
  bidErrorMessage: string = '';
  attachments: MeetingAttachment[] = []; // Meeting-level attachments
  showAttachmentsDialog: boolean = false; // Dialog for meeting documents
  isLoadingAttachments: boolean = false;
  reviewCommentsInput: string = '';
  isReviewing: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private _http: HttpClient,
    private _cdr: ChangeDetectorRef,
    private userService: UserService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this._route.params.subscribe(params => {
      this.evaluationId = params['id'];
      if (this.evaluationId) {
        this.fetchEvaluationDetails();
      }
    });
  }

  
  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL;
    return apiService;
  }

async fetchEvaluationDetails(): Promise<void> {
  this._loaderService.showLoader();

  try {
    const apiService = this.createApiService();
    const req$ = apiService.get(
      AppConstants.CONTROLLERS.EVALUATIONS,
      `${AppConstants.ACTIONS.GetEvaluationDetailsById}/${this.evaluationId}`,
      true,
      environment.APIs_PREFIX
    );

    const response: any = await lastValueFrom(req$ as any);

    if (response?.isSuccess === false) {
      this.bidErrorMessage =
        response?.errorMessageEn ||
        response?.errorMessageAr ||
        'Failed to fetch evaluation details';

      this.evaluationDetails = null;
      return;
    }

    this.evaluationDetails =
      this.mapApiResponseToEvaluationDetails(response);

    console.log('Evaluation Details Loaded:', this.evaluationDetails);
    console.log('Assigned Evaluators:', this.evaluationDetails?.assignmentInfo?.assignedEvaluators);

    this.initializeBidsTable();
    this.loadEvaluationAttachments();

    this.bidErrorMessage = '';
    this._cdr.detectChanges();

  } catch (error: any) {
    console.error('Failed to fetch evaluation details:', error);
    this.bidErrorMessage =
      error?.error?.errorMessageEn ||
      error?.error?.errorMessageAr ||
      error?.message ||
      'Failed to fetch evaluation details';

    this.evaluationDetails = null;
  } finally {
    this._loaderService.hideLoader();
  }
}

private mapApiResponseToEvaluationDetails(apiData: any): EvaluationDetails {
  return {
    evaluationId: apiData.evaluationId ?? '',
    tenderId: apiData.tenderId ?? '',
    prNumber: apiData.prNumber ?? '',
    tenderTitle: apiData.tenderTitle ?? '',
    evaluationType: apiData.evaluationType ?? '',
    evaluatorId: apiData.evaluatorId ?? '',
    comments: apiData.comments ?? '',
    recommendation: apiData.recommendation ?? '',
    status: apiData.status ?? '',
    reviewComments: apiData.reviewComments ?? '',
    score: apiData.score ?? 0,

    assignmentInfo: {
      assignedToEn: apiData.assignmentInfo?.assignedToEn ?? '',
      assignedToAr: apiData.assignmentInfo?.assignedToAr ?? '',
      assignedDate: apiData.assignmentInfo?.assignedDate ?? '',
      submittedDate: apiData.assignmentInfo?.submittedDate ?? '',
      assignedEvaluators: (apiData.assignmentInfo?.assignedEvaluators ?? []).map((e: any) => ({
        userId: e.userId,
        fullName: e.fullName,
        nameAr: e.nameAr,
        email: e.email
      }))
    },

    timelineEvents: (apiData.timelineEvents ?? []).map((t: any) => ({
      title: t.title ?? '',
      date: t.date ?? '',
      type: t.type as 'submitted' | 'started' | 'assigned' | 'accepted'
    })),

    allBids: (apiData.allBids ?? []).map((b: any) => ({
      bidId: b.bidID,
      vendorName: b.vendorName,
      generalRemarks: b.generalRemarks ?? '',
      status: b.status,
      actionTaken: b.actionTaken,
      isCurrent: b.isCurrent
    }))
  };
}

  initializeBidsTable(): void {
  this.totalBids = this.evaluationDetails?.allBids?.length ?? 0;

  this.tableColumns = [
    {
      field: 'bidId',
      header: 'Bid ID',
      type: 'text',
      showCurrentTag: true
    },
    {
      field: 'vendorName',
      header: 'Vendor',
      type: 'text'
    },
    {
      field: 'generalRemarks',
      header: 'General Remarks',
      type: 'text'
    },
    {
      field: 'actionTaken',
      header: 'Action',
      type: 'badge',
      badgeColor: (row: BidInfo) => {
        if (row.actionTaken === 'Approved') return 'green';
        if (row.actionTaken === 'Rejected') return 'red';
        if (row.actionTaken === 'Pending') return 'blue';
        return 'gray';
      }
    }
  ];
}


  onTableActionClick(event: {action: string, row: any}): void {
    if (event.action === 'approve') {
      this.onApproveBid(event.row);
    } else if (event.action === 'reject') {
      this.onRejectBid(event.row);
    }
  }

  isSecretary(): boolean {
    const role = this.userService.user?.Role;
    const roleValue = typeof role === 'string' && !isNaN(Number(role)) ? Number(role) : role;
    return role === 'Secretary' || role === 'secretary' || role === 'Tender Committee Secretary' || roleValue === 4;
  }

  canReviewEvaluation(): boolean {
    return this.isSecretary() && this.evaluationDetails?.status?.toLowerCase() === 'evaluated';
  }

  async reviewEvaluation(isApproved: boolean): Promise<void> {
    if (!this.evaluationDetails?.evaluationId || this.isReviewing) {
      return;
    }

    const comments = this.reviewCommentsInput.trim();
    if (!isApproved && !comments) {
      this.toasterService.showError('Comments are required when requesting changes.', 'Validation Error');
      return;
    }

    this.isReviewing = true;
    this._loaderService.showLoader();
    try {
      const response: any = await lastValueFrom(this.apiService.postByUrl(
        `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.ReviewEvaluation}`,
        {
          evaluationId: this.evaluationDetails.evaluationId,
          isApproved,
          comments
        }
      ));

      if (response?.isSuccess) {
        this.toasterService.showSuccess(
          isApproved ? 'Evaluation accepted successfully.' : 'Change request sent to the evaluator.',
          'Success'
        );
        await this.fetchEvaluationDetails();
        this.reviewCommentsInput = '';
        return;
      }

      this.toasterService.showError(response?.errorMessageEn || 'Unable to review the evaluation.', 'Error');
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.message || 'Unable to review the evaluation.', 'Error');
    } finally {
      this.isReviewing = false;
      this._loaderService.hideLoader();
    }
  }

  onApproveBid(bid: BidInfo): void {
    console.log('Bid approved:', bid);
    // TODO: Implement approve bid functionality
  }

  onRejectBid(bid: BidInfo): void {
    console.log('Bid rejected:', bid);
    // TODO: Implement reject bid functionality
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
  }

  getStatusColor(status: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    switch(status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'in progress':
      case 'under evaluation':
        return 'blue';
      case 'pending':
      case 'pending evaluation':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  }

  getEvaluationTypeColor(type: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    switch(type?.toLowerCase()) {
      case 'technical':
        return 'blue';
      case 'financial':
        return 'green';
      case 'commercial':
        return 'purple';
      case 'combined':
        return 'orange';
      default:
        return 'gray';
    }
  }

  getTimelineDotColor(type: string): string {
    switch(type) {
      case 'submitted':
        return '#00A63E'; // green
      case 'started':
        return '#532E60'; // blue/purple
      case 'assigned':
        return '#6A7282'; // gray
      case 'accepted':
        return '#00A63E'; // green
      default:
        return '#6A7282';
    }
  }

  async exportPDF(): Promise<void> {
    if (!this.evaluationDetails) {
      console.error('No evaluation details available');
      return;
    }

    const doc = new jsPDF();
    const margin = 20;
    const maxWidth = doc.internal.pageSize.getWidth() - (margin * 2);
    let y = 20;

    const addText = (text: string, size: number, bold = false, x = margin, yPos = y) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.text(text, x, yPos);
      return yPos + (size * 0.5);
    };

    // Header
    y = addText('Evaluation Details', 20, true);
    y += 5;

    // Evaluation Details
    if (this.evaluationDetails) {
      y = addText(`Evaluation ID: ${this.evaluationDetails.evaluationId}`, 14, false, margin, y);
      y = addText(`PR: ${this.evaluationDetails.prNumber}`, 12, false, margin, y);
      y = addText(`Evaluation Type: ${this.evaluationDetails.evaluationType}`, 12, false, margin, y);
      y = addText(`Status: ${this.evaluationDetails.status}`, 12, false, margin, y);
      y = addText(`Tender Title: ${this.evaluationDetails.tenderTitle}`, 12, false, margin, y);
      y = addText(`Overall Score: ${this.evaluationDetails.score} / 100`, 12, false, margin, y);
    }

    doc.save(`Evaluation_Details_${this.evaluationId}.pdf`);
  }
  
  getBidErrorMessage(): string {
    return this.bidErrorMessage;
  }
  
  openAttachmentsDialog(): void {
    this.showAttachmentsDialog = true;
    // Ensure attachments are loaded
    if (this.attachments.length === 0 && !this.isLoadingAttachments) {
      this.loadEvaluationAttachments();
    }
  }

  closeAttachmentsDialog(): void {
    this.showAttachmentsDialog = false;
  }

  async loadEvaluationAttachments(): Promise<void> {
    if (!this.evaluationDetails?.evaluationId) {
      this.attachments = [];
      return;
    }

    this.isLoadingAttachments = true;
    this.attachments = [];

    try {
      // Fetch documents for this meeting
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.evaluationDetails?.evaluationId.toString(),
        processName: 'Evaluation',
        processID: this.evaluationDetails?.evaluationId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs)
          const USE_DIRECT_BLOB_ACCESS = true;
          
          // Process documents and fetch file sizes
          const attachmentPromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              // Format file size
              const formatFileSize = (bytes: number): string => {
                if (!bytes || bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
              };

              // Get file URL - use SAS URL if available, otherwise use download endpoint
              let fileUrl: string;
              if (USE_DIRECT_BLOB_ACCESS && doc.fileContent) {
                fileUrl = doc.fileContent; // SAS URL
              } else {
                fileUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
              }

              // Fetch file to get actual size
              let fileSize = 0;
              try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10);
                } else {
                  // If HEAD doesn't work, fetch the blob to get size
                  const blobResponse = await fetch(fileUrl);
                  const blob = await blobResponse.blob();
                  fileSize = blob.size;
                }
              } catch (error) {
                console.warn(`Could not fetch file size for ${doc.name}:`, error);
                // Try to use doc.size if available as fallback
                fileSize = doc.size || doc.fileSize || 0;
              }

              // Clean filename - remove attachment IDs and UUIDs
              let cleanFileName = doc.name || 'document';
              const extension = cleanFileName.includes('.') ? cleanFileName.split('.').pop() || '' : '';
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
              const hasUUIDs = uuidPattern.test(cleanFileName);
              
              if (hasUUIDs) {
                cleanFileName = extension ? `document.${extension}` : 'document';
              }

              return {
                name: cleanFileName,
                originalName: doc.name,
                url: fileUrl,
                size: formatFileSize(fileSize),
                fileType: doc.mimeType || 'application/octet-stream',
                uploadedBy: doc.uploadedBy || 'System',
                uploadedDate: doc.uploadedDate || '',
                description: doc.description || '',
                date: doc.date || new Date(),
                attachmentFor: doc.attachmentFor || '',
                fileContent: doc.fileContent || '',
                additionalDetails: doc.additionalDetails || '',
                mimeType: doc.mimeType || 'application/octet-stream'
              } as MeetingAttachment;
            });

          // Wait for all attachments to be processed
          this.attachments = await Promise.all(attachmentPromises);
        }
      }
    } catch (error) {
      console.error('Error loading meeting attachments:', error);
      this.attachments = [];
    } finally {
      this.isLoadingAttachments = false;
      this._cdr.detectChanges()
    }
  }

  trackByAttachment(index: number, attachment: MeetingAttachment): string {
    return attachment.name || index.toString();
  }

  async previewAttachment(attachment: MeetingAttachment): Promise<void> {
    try {
      if (!attachment.url || attachment.url === '#') {
        throw new Error('Invalid attachment URL');
      }

      // Detect Safari/iPad - Safari blocks window.open() after async operations
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // For Safari/iPad: Open the original URL directly in a new tab (no blank page)
      if (isSafari) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // For Chrome/other browsers: Fetch as blob to prevent downloads
      // Fetch the file as a blob to create an object URL for preview
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const fileType = (attachment as any).fileType || attachment.mimeType || 'application/octet-stream';
      const file = new File([blob], attachment.name, { type: fileType });
      const fileURL = URL.createObjectURL(file);
      
      // Open in new tab with file loaded
      window.open(fileURL, '_blank', 'noopener,noreferrer');
      
      // Don't revoke the blob URL - let browser handle cleanup when tab closes
    } catch (error) {
      console.error('Error previewing attachment:', error);
    }
  }

  async downloadFile(attachment: MeetingAttachment): Promise<void> {
    try {
      if (!attachment.url || attachment.url === '#') {
        throw new Error('Invalid attachment URL');
      }

      // Fetch the file as a blob
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a File object with the clean filename (without IDs)
      const fileType = (attachment as any).fileType || attachment.mimeType || 'application/octet-stream';
      const file = new File([blob], attachment.name, { type: fileType });
      
      // Create object URL and trigger download with clean filename
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = attachment.name; // Use clean filename (without IDs)
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  }
  
}

